import {
  Badge,
  Button,
  Card,
  Divider,
  Empty,
  Popconfirm,
  Result,
  Select,
  Spin,
  Tabs,
  Typography,
  notification,
} from "antd";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import useApi from "../../utils/useApi";
import CameraFormModal from "./CameraFormModal";
import CameraModal from "./CameraModal";
import CamerasTab from "./CamerasTab";
import {
  GadgetDisco,
  GadgetFila,
  GadgetHeartbeat,
  GadgetRelogio,
  GadgetUpload,
} from "./Gadgets";
import ParametrosTab from "./ParametrosTab";
import TokenModal from "./TokenModal";
import { podeVerGateway } from "./acesso";
import {
  descreverErro,
  fraseDoGateway,
  resumoDasCameras,
  textoDaDecisao,
} from "./textos";

const { Text, Title } = Typography;

const CORES_DA_SITUACAO = {
  online: "success",
  silencioso: "error",
  aguardando_registro: "processing",
  sem_gateway: "default",
};

function avisarErro(erro, mensagem) {
  notification.error({
    message: mensagem,
    description: descreverErro(erro, "Tente de novo em instantes."),
  });
}

export default function ConexaoCamerasGateway() {
  const { user } = useAuth();
  const podeVer = podeVerGateway(user);
  const api = useApi();
  const queryClient = useQueryClient();
  const [filialId, setFilialId] = useState(undefined);
  const [aba, setAba] = useState("cameras");
  const [token, setToken] = useState(null);
  const [cameraAberta, setCameraAberta] = useState(null);
  const [formulario, setFormulario] = useState({ aberto: false, camera: null });
  const [agora, setAgora] = useState(new Date());

  useEffect(() => {
    const relogio = setInterval(() => setAgora(new Date()), 5000);
    return () => clearInterval(relogio);
  }, []);

  const { data: filiais = [] } = useQuery({
    queryKey: ["gateway-filiais"],
    queryFn: api.getGatewayFiliais,
    enabled: podeVer,
    refetchInterval: 30000,
  });

  useEffect(() => {
    if (filialId !== undefined || filiais.length === 0) return;
    const comGateway = filiais.find((f) => f.situacao !== "sem_gateway");
    setFilialId((comGateway || filiais[0]).id);
  }, [filiais, filialId]);

  const chave = ["gateway-filial", filialId];
  const { data: estado, isLoading } = useQuery({
    queryKey: chave,
    queryFn: () => api.getGatewayFilial(filialId),
    enabled: podeVer && Boolean(filialId),
    refetchInterval: 15000,
  });

  const aplicar = (novo) => {
    if (novo) queryClient.setQueryData(chave, novo);
    queryClient.invalidateQueries({ queryKey: ["gateway-filiais"] });
    queryClient.invalidateQueries({ queryKey: ["gateway-camera", filialId] });
  };

  const gerarToken = useMutation({
    mutationFn: () => api.generateGatewayToken(filialId),
    onSuccess: (dados) => {
      setToken(dados.token);
      queryClient.invalidateQueries({ queryKey: chave });
      aplicar();
    },
    onError: (e) => avisarErro(e, "Não foi possível gerar o token"),
  });

  const revogarToken = useMutation({
    mutationFn: () => api.revokeGatewayToken(filialId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chave });
      aplicar();
    },
    onError: (e) => avisarErro(e, "Não foi possível revogar o token"),
  });

  const decidirGeral = useMutation({
    mutationFn: (parada) => api.decideGatewayGravacao(filialId, parada),
    onSuccess: aplicar,
    onError: (e) => avisarErro(e, "Não foi possível mudar a gravação"),
  });

  const salvarParametros = useMutation({
    mutationFn: (dados) => api.updateGatewayParametros(filialId, dados),
    onSuccess: (novo) => {
      aplicar(novo);
      notification.success({ message: "Parâmetros salvos" });
    },
    onError: (e) => avisarErro(e, "Não foi possível salvar os parâmetros"),
  });

  const salvarCamera = useMutation({
    mutationFn: ({ camera, dados }) =>
      camera
        ? api.updateGatewayCamera(filialId, camera.id, dados)
        : api.createGatewayCamera(filialId, dados),
    onSuccess: (novo) => {
      aplicar(novo);
      setFormulario({ aberto: false, camera: null });
    },
    onError: (e) => avisarErro(e, "Não foi possível salvar a câmera"),
  });

  const removerCamera = useMutation({
    mutationFn: (camera) => api.deleteGatewayCamera(filialId, camera.id),
    onSuccess: aplicar,
    onError: (e) => avisarErro(e, "Não foi possível remover a câmera"),
  });

  const decidirCamera = useMutation({
    mutationFn: ({ camera, parada }) =>
      api.decideGatewayCameraGravacao(filialId, camera.id, parada),
    onSuccess: aplicar,
    onError: (e) => avisarErro(e, "Não foi possível mudar a gravação"),
  });

  if (!podeVer) {
    return (
      <Result
        status="403"
        title="Acesso restrito"
        subTitle="Esta área ainda não está liberada para o seu usuário."
      />
    );
  }

  const gateway = estado?.gateway;
  const temGateway = gateway && gateway.situacao !== "sem_gateway";
  const aguardando = temGateway && !gateway.configuracaoAplicada;
  const frase = fraseDoGateway(estado, agora);
  const heartbeat = gateway?.heartbeat;
  const paradaGeral = estado?.gravacao?.parada;
  const uploadPausado = estado?.parametros?.pausarUpload;

  const abrirFormulario = (camera) => {
    setCameraAberta(null);
    setFormulario({ aberto: true, camera });
  };

  return (
    <div style={{ padding: 24, maxWidth: 1440 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          marginBottom: 16,
        }}>
        <Title level={4} style={{ margin: 0 }}>
          Conexão câmeras gateway
        </Title>
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Filial"
          style={{ minWidth: 360 }}
          value={filialId}
          onChange={setFilialId}
          options={filiais.map((f) => ({
            value: f.id,
            label: `${f.cliente} · ${f.nome}`,
            situacao: f.situacao,
          }))}
          optionRender={(opcao) => (
            <span>
              <Badge status={CORES_DA_SITUACAO[opcao.data.situacao]} />{" "}
              {opcao.label}
            </span>
          )}
        />
      </div>

      {isLoading || !estado ? (
        <Card>
          {filialId ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spin />
            </div>
          ) : (
            <Empty description="Nenhuma filial" />
          )}
        </Card>
      ) : (
        <>
          {!temGateway ? (
            <Card style={{ marginBottom: 16 }}>
              <div style={{ textAlign: "center", padding: "24px 20px" }}>
                <Title level={5} style={{ marginTop: 0 }}>
                  Nenhum gateway conectado
                </Title>
                <Text type="secondary">
                  Gere o token e cole no gateway instalado na loja. Ele baixa as
                  câmeras desta filial e começa a gravar no horário da loja.
                </Text>
                <div style={{ marginTop: 16 }}>
                  <Button
                    type="primary"
                    loading={gerarToken.isPending}
                    onClick={() => gerarToken.mutate()}>
                    Gerar token
                  </Button>
                </div>
              </div>
            </Card>
          ) : (
            <>
              <Card style={{ marginBottom: 16 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 16,
                  }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>
                      <Badge status={frase.cor} /> {frase.texto}
                    </div>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {[
                        gateway.nomeMaquina,
                        gateway.so,
                        gateway.versao && `versão ${gateway.versao}`,
                        resumoDasCameras(estado.cameras),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </Text>
                  </div>
                  <div
                    style={{
                      marginLeft: "auto",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                    }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      Token{" "}
                      <Text code>
                        {gateway.token.valido
                          ? `${gateway.token.prefixo}…${gateway.token.ultimos4}`
                          : "revogado"}
                      </Text>
                    </Text>
                    <Popconfirm
                      title="Gerar um novo token?"
                      description="O token atual para de funcionar na hora."
                      okText="Gerar"
                      cancelText="Cancelar"
                      onConfirm={() => gerarToken.mutate()}>
                      <Button loading={gerarToken.isPending}>
                        Gerar novo token
                      </Button>
                    </Popconfirm>
                    {gateway.token.valido && (
                      <Popconfirm
                        title="Revogar o token?"
                        description="O gateway para de falar com o Ouran até receber um token novo."
                        okText="Revogar"
                        cancelText="Cancelar"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => revogarToken.mutate()}>
                        <Button danger loading={revogarToken.isPending}>
                          Revogar
                        </Button>
                      </Popconfirm>
                    )}
                  </div>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1.7fr 1fr 1.4fr",
                    gap: 12,
                    marginTop: 14,
                  }}>
                  <GadgetHeartbeat
                    historico={gateway.historico}
                    ultimoHeartbeatEm={gateway.ultimoHeartbeatEm}
                    agora={agora}
                  />
                  <GadgetRelogio relogio={heartbeat?.relogio} />
                  <GadgetDisco disco={heartbeat?.disco} />
                  <GadgetFila
                    fila={heartbeat?.fila}
                    historico={gateway.historico}
                    agora={agora}
                  />
                  <GadgetUpload
                    upload={heartbeat?.upload}
                    historico={gateway.historico}
                  />
                </div>
              </Card>

              <Card style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <div>
                    <Text strong>Gravação da filial</Text>
                    <div>
                      <Text type="secondary">
                        {paradaGeral
                          ? textoDaDecisao(estado.gravacao, agora)
                          : `Gravando. ${textoDaDecisao(
                              estado.gravacao,
                              agora
                            )}.`}
                      </Text>
                    </div>
                    {aguardando && (
                      <Text type="warning" style={{ fontSize: 13 }}>
                        Solicitado, aguardando o gateway (até 60 s)
                      </Text>
                    )}
                  </div>
                  <Button
                    danger={!paradaGeral}
                    loading={decidirGeral.isPending}
                    onClick={() => decidirGeral.mutate(!paradaGeral)}>
                    {paradaGeral ? "Retomar tudo" : "Parar tudo"}
                  </Button>
                  <Divider type="vertical" style={{ height: 40 }} />
                  <div>
                    <Text strong>Envio</Text>
                    <div>
                      <Text type="secondary">
                        {uploadPausado
                          ? "Envio pausado"
                          : "Enviando normalmente"}
                      </Text>
                    </div>
                  </div>
                  <Button
                    loading={salvarParametros.isPending}
                    onClick={() =>
                      salvarParametros.mutate({ pausarUpload: !uploadPausado })
                    }>
                    {uploadPausado ? "Retomar upload" : "Pausar upload"}
                  </Button>
                </div>
              </Card>
            </>
          )}

          <Card styles={{ body: { paddingTop: 0 } }}>
            <Tabs
              activeKey={aba}
              onChange={setAba}
              items={[
                {
                  key: "cameras",
                  label: `Câmeras (${estado.cameras.length})`,
                  children: (
                    <CamerasTab
                      cameras={estado.cameras}
                      aguardando={aguardando}
                      onAbrir={(c) => setCameraAberta(c.id)}
                      onAdicionar={() => abrirFormulario(null)}
                      onEditar={abrirFormulario}
                      onRemover={(c) => removerCamera.mutate(c)}
                      onDecidir={(c, parada) =>
                        decidirCamera.mutate({ camera: c, parada })
                      }
                      decidindoId={
                        decidirCamera.isPending
                          ? decidirCamera.variables?.camera?.id
                          : null
                      }
                    />
                  ),
                },
                {
                  key: "parametros",
                  label: "Parâmetros",
                  children: (
                    <ParametrosTab
                      parametros={estado.parametros}
                      salvando={salvarParametros.isPending}
                      onSalvar={(dados) => salvarParametros.mutate(dados)}
                    />
                  ),
                },
              ]}
            />
          </Card>
        </>
      )}

      <CameraModal
        filialId={filialId}
        cameraId={cameraAberta}
        aguardando={aguardando}
        decidindo={decidirCamera.isPending}
        onFechar={() => setCameraAberta(null)}
        onEditar={abrirFormulario}
        onDecidir={(c, parada) => decidirCamera.mutate({ camera: c, parada })}
      />
      <CameraFormModal
        aberto={formulario.aberto}
        camera={formulario.camera}
        zonasDaFilial={estado?.zonasDaFilial}
        linhasDaFilial={estado?.linhasDaFilial}
        salvando={salvarCamera.isPending}
        onFechar={() => setFormulario({ aberto: false, camera: null })}
        onSalvar={(dados) =>
          salvarCamera.mutate({ camera: formulario.camera, dados })
        }
      />
      <TokenModal token={token} onFechar={() => setToken(null)} />
    </div>
  );
}
